import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Shield, Layers, RefreshCw } from "lucide-react";

const API_BASE_URL = process.env.REACT_APP_API_URL || "";

interface PageRow {
  pageId: string;
  name: string;
  isActive: boolean;
  isConnected: boolean;
  disconnectedAt?: string | null;
  owner: { id: string; name: string; email: string } | null;
  aiSettings: {
    autoRepliesEnabled: boolean;
    commentModerationEnabled: boolean;
    aiMode: string;
  };
  webhook: { isSubscribed: boolean; lastWebhookReceived: string | null };
}

interface HealthRow {
  pageId: string;
  tokenValid: boolean;
  errors24h: number;
  webhook: { isSubscribed: boolean; lastWebhookReceived: string | null; stale: boolean };
}

const AdminPages: React.FC = () => {
  const navigate = useNavigate();
  const [pages, setPages] = useState<PageRow[]>([]);
  const [health, setHealth] = useState<Record<string, HealthRow>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<"all" | "connected" | "disconnected" | "errors">("all");

  const headers = useMemo((): Record<string, string> => {
    const token = localStorage.getItem("adminToken");
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [pagesRes, healthRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/admin/pages`, { headers }),
        fetch(`${API_BASE_URL}/api/admin/pages/health`, { headers }),
      ]);
      if (pagesRes.status === 401) {
        localStorage.removeItem("adminToken");
        localStorage.removeItem("adminUser");
        navigate("/admin/login");
        return;
      }
      if (!pagesRes.ok) throw new Error(`Pages: ${pagesRes.status}`);
      const pagesData = await pagesRes.json();
      setPages(pagesData.pages || []);
      if (healthRes.ok) {
        const healthData = await healthRes.json();
        const map: Record<string, HealthRow> = {};
        (healthData.pages || []).forEach((h: HealthRow) => { map[h.pageId] = h; });
        setHealth(map);
      }
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [headers, navigate]);

  useEffect(() => { load(); }, [load]);

  const filtered = pages.filter((p) => {
    if (query) {
      const q = query.toLowerCase();
      if (
        !p.name?.toLowerCase().includes(q) &&
        !p.pageId.includes(q) &&
        !p.owner?.name?.toLowerCase().includes(q) &&
        !p.owner?.email?.toLowerCase().includes(q)
      ) return false;
    }
    if (filter === "connected" && !p.isConnected) return false;
    if (filter === "disconnected" && p.isConnected) return false;
    if (filter === "errors" && !(health[p.pageId]?.errors24h > 0)) return false;
    return true;
  });

  const formatDate = (s: string | null | undefined) => (s ? new Date(s).toLocaleString() : "—");

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto p-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-semibold text-gray-800 flex items-center gap-2">
            <Shield className="h-6 w-6" /> Admin Panel
          </h1>
          <div className="flex gap-2">
            <button onClick={() => navigate("/admin/dashboard")} className="px-3 py-1 rounded bg-white border">Dashboard</button>
            <button onClick={() => navigate("/admin/users")} className="px-3 py-1 rounded bg-white border">Users</button>
            <button onClick={() => navigate("/admin/pages")} className="px-3 py-1 rounded bg-gray-900 text-white"><Layers className="inline h-4 w-4 mr-1" />Pages</button>
            <button onClick={load} className="px-3 py-1 rounded bg-white border" title="Reload"><RefreshCw className="inline h-4 w-4" /></button>
          </div>
        </div>

        <div className="flex gap-2 items-end mb-4">
          <div>
            <label className="text-xs text-gray-500">Search</label>
            <input
              className="border rounded px-2 py-1 w-72"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="page name, id, owner name or email"
            />
          </div>
          <div>
            <label className="text-xs text-gray-500">Filter</label>
            <select
              className="border rounded px-2 py-1"
              value={filter}
              onChange={(e) => setFilter(e.target.value as any)}
            >
              <option value="all">All</option>
              <option value="connected">Connected</option>
              <option value="disconnected">Disconnected</option>
              <option value="errors">With errors (24h)</option>
            </select>
          </div>
          <div className="text-xs text-gray-600 ml-auto pb-1">{filtered.length} / {pages.length} pages</div>
        </div>

        {error && <div className="mb-4 p-3 rounded bg-red-50 border border-red-200 text-red-800 text-sm">{error}</div>}

        {loading ? (
          <div className="p-6 text-gray-500">Loading…</div>
        ) : (
          <div className="bg-white rounded border overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left">
                <tr>
                  <th className="p-2">Page</th>
                  <th className="p-2">Owner</th>
                  <th className="p-2">Connected</th>
                  <th className="p-2">Token</th>
                  <th className="p-2">Webhook</th>
                  <th className="p-2">AI</th>
                  <th className="p-2">Errors (24h)</th>
                  <th className="p-2">Last event</th>
                  <th className="p-2"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => {
                  const h = health[p.pageId];
                  return (
                    <tr key={p.pageId} className="border-t">
                      <td className="p-2">
                        <div className="font-medium text-gray-800">{p.name || "(unnamed)"}</div>
                        <div className="text-xs text-gray-500 font-mono">{p.pageId}</div>
                      </td>
                      <td className="p-2 text-xs text-gray-600">
                        {p.owner ? (<>{p.owner.name}<br /><span className="text-gray-500">{p.owner.email}</span></>) : "—"}
                      </td>
                      <td className="p-2"><Pill ok={p.isConnected} good="Yes" bad="No" /></td>
                      <td className="p-2"><Pill ok={!!h?.tokenValid} good="Valid" bad="Invalid" /></td>
                      <td className="p-2">
                        <Pill ok={!!h?.webhook.isSubscribed && !h?.webhook.stale} good="OK" bad={h?.webhook.isSubscribed ? "Stale" : "Off"} />
                      </td>
                      <td className="p-2 text-xs">
                        <div>Mod: <span className={p.aiSettings.commentModerationEnabled ? "text-green-700" : "text-red-700"}>{p.aiSettings.commentModerationEnabled ? "on" : "off"}</span></div>
                        <div>Reply: <span className={p.aiSettings.autoRepliesEnabled ? "text-green-700" : "text-red-700"}>{p.aiSettings.autoRepliesEnabled ? "on" : "off"}</span></div>
                      </td>
                      <td className={`p-2 font-medium ${h?.errors24h ? "text-red-600" : "text-gray-700"}`}>{h?.errors24h ?? 0}</td>
                      <td className="p-2 text-xs text-gray-600 whitespace-nowrap">{formatDate(p.webhook.lastWebhookReceived)}</td>
                      <td className="p-2">
                        <button
                          className="px-2 py-1 bg-gray-900 text-white rounded text-xs"
                          onClick={() => navigate(`/admin/pages/${p.pageId}`)}
                        >
                          Open
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr><td className="p-4 text-center text-gray-500" colSpan={9}>No pages match.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const Pill: React.FC<{ ok: boolean; good: string; bad: string }> = ({ ok, good, bad }) => (
  <span className={`px-2 py-0.5 rounded text-xs ${ok ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
    {ok ? good : bad}
  </span>
);

export default AdminPages;
